import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { ChartLine, ChatCircleDots, Package, CurrencyCircleDollar, TrendUp, Robot, CheckCircle, EnvelopeSimple, ShoppingBag, Truck, Warning, Fire, BellRinging } from '@phosphor-icons/react';

const Dashboard = () => {
  const { selectedBusiness } = useOutletContext();
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inactiveAlerts, setInactiveAlerts] = useState([]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (!isAdmin && user?.role !== 'supervisor') return;
    api.get('/work-plan/alerts/inactive-vendors')
      .then((r) => setInactiveAlerts(r.data.alerts || []))
      .catch(() => {});
  }, [isAdmin, user?.role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, convRes] = await Promise.all([
        api.get(`/stats/${selectedBusiness.id}/enhanced`),
        api.get('/conversations', { params: { business_id: selectedBusiness.id } })
      ]);
      setStats(statsRes.data);
      setRecentActivity(convRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--bg)]">
        <p className="text-sm text-slate-400">Selecciona un negocio para continuar</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--bg)]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // Hot leads computation: conversations unread > 1h
  const hotLeads = recentActivity.filter(c => {
    if (!c.unread_count || c.unread_count === 0) return false;
    if (!c.last_message_time) return false;
    const ageMs = Date.now() - new Date(c.last_message_time).getTime();
    return ageMs > 60 * 60 * 1000; // 1h
  }).length;

  return (
    <div className="h-full p-4 md:p-8 overflow-y-auto bg-[var(--bg)]" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-black text-slate-500 mb-2">
            {selectedBusiness.name}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-white">
            Hola, {user?.name?.split(' ')[0] || 'Equipo'} <span className="text-blue-500">.</span>
          </h1>
        </div>
      </div>

      {/* Inactive vendor alerts (supervisor/admin only) */}
      {inactiveAlerts.length > 0 && (
        <Link
          to="/plan-trabajo"
          className="block bg-red-500/10 dark:bg-red-500/15 border border-red-500/30 rounded-[14px] p-4 mb-6 hover:bg-red-500/15 dark:hover:bg-red-500/20 transition-colors shadow-[var(--shadow-sm)]"
          data-testid="dashboard-inactive-alerts"
        >
          <div className="flex items-center gap-3">
            <BellRinging size={22} weight="fill" className="text-red-500 animate-pulse shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-red-600 dark:text-red-400">
                {inactiveAlerts.length} vendedor{inactiveAlerts.length === 1 ? '' : 'es'} sin actividad reciente
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">
                {inactiveAlerts.slice(0, 5).map((a) => a.user_name).join(', ')}
                {inactiveAlerts.length > 5 ? ` y ${inactiveAlerts.length - 5} más` : ''} · clic para revisar
              </p>
            </div>
          </div>
        </Link>
      )}

      {/* Strategic Header Metrics — 4 KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* DINERO HOY (verde) */}
        <MetricCard
          glow="green"
          icon={CurrencyCircleDollar}
          label="DINERO HOY"
          value={`Q${(stats?.today_revenue || stats?.total_revenue || 0).toFixed(0)}`}
          subtitle={`${stats?.today_orders || 0} pedidos · ticket Q${stats?.avg_order_value || 0}`}
          testId="metric-money-today"
        />

        {/* LEADS CALIENTES (naranja) */}
        <MetricCard
          glow="orange"
          icon={Fire}
          label="LEADS CALIENTES"
          value={hotLeads}
          subtitle="Sin atender +1h"
          testId="metric-hot-leads"
          pulse={hotLeads > 0}
        />

        {/* SIN RESPONDER (rojo) */}
        <MetricCard
          glow="red"
          icon={EnvelopeSimple}
          label="SIN RESPONDER"
          value={stats?.total_unread_messages || 0}
          subtitle={`${stats?.unread_conversations || 0} conversaciones`}
          testId="metric-unanswered"
        />

        {/* CONVERSIÓN (azul) */}
        <MetricCard
          glow="blue"
          icon={TrendUp}
          label="CONVERSIÓN"
          value={`${stats?.conversion_rate || 0}%`}
          subtitle="Conv. → Pedidos"
          testId="metric-conversion"
        />
      </div>

      {/* Alert: Unread messages */}
      {stats?.total_unread_messages > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-[16px] mb-6 shadow-[0_0_20px_rgba(239,68,68,0.1)]" data-testid="unread-alert">
          <div className="p-2 rounded-[12px] bg-red-500/20">
            <Warning size={20} weight="fill" className="text-red-400" />
          </div>
          <div>
            <p className="text-sm text-red-400 font-bold">
              {stats.total_unread_messages} mensajes sin responder
            </p>
            <p className="text-xs text-red-300/80">
              en {stats.unread_conversations} conversaciones · responde rápido
            </p>
          </div>
        </div>
      )}

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SecondaryCard icon={ChatCircleDots} title="Conv. Abiertas" value={stats?.open_conversations || 0} testId="stat-open-conversations" />
        <SecondaryCard icon={Package} title="Pedidos Pendientes" value={stats?.pending_orders || 0} testId="stat-pending-orders" />
        <SecondaryCard icon={ChartLine} title="Total Conv." value={stats?.total_conversations || 0} testId="stat-total-conversations" />
        <SecondaryCard icon={Truck} title="Entregados" value={stats?.delivered_orders || 0} testId="stat-delivered" />
      </div>

      {/* Performance Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <PerfCard icon={CheckCircle} colorClass="green" title="Pedidos Hoy" value={stats?.today_orders || 0} note={`Total: ${stats?.total_orders || 0}`} testId="today-orders-card" />
        <PerfCard icon={CurrencyCircleDollar} colorClass="blue" title="Ticket Promedio" value={`Q${stats?.avg_order_value || 0}`} note="Por pedido" testId="avg-order-card" />
        <PerfCard icon={Robot} colorClass="orange" title="Bot IA" value={stats?.catalog_products > 0 ? 'Activo' : 'Sin catálogo'} note={`${stats?.catalog_products || 0} productos`} testId="bot-card" />
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[16px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-black tracking-tight text-white">Actividad Reciente</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Últimas 5</span>
        </div>
        {recentActivity.length === 0 ? (
          <div className="py-8 text-center">
            <ChatCircleDots size={32} className="text-slate-700 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No hay actividad reciente</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentActivity.map((conv) => (
              <div key={conv.id} className="flex items-center justify-between p-4 bg-white/[0.02] border border-[var(--border)] rounded-[12px] hover:bg-white/[0.04] hover:border-[var(--border)] transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${conv.unread_count > 0 ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.6)]' : 'bg-slate-700'}`}></div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{conv.last_message || 'Sin mensajes'}</p>
                    {conv.unread_count > 0 && (
                      <span className="text-[10px] font-black text-red-400 uppercase tracking-wide">{conv.unread_count} sin leer</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wide ${
                    conv.platform === 'whatsapp'
                      ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                      : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  }`}>{conv.platform}</span>
                  <p className="text-[10px] text-slate-500 mt-1">
                    {new Date(conv.last_message_time).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const glowClasses = {
  green: 'border-green-500/20 shadow-[0_0_30px_rgba(16,185,129,0.15)]',
  orange: 'border-orange-500/20 shadow-[0_0_30px_rgba(249,115,22,0.15)]',
  red: 'border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.15)]',
  blue: 'border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.15)]',
};
const iconBg = {
  green: 'bg-green-500/15 text-green-400',
  orange: 'bg-orange-500/15 text-orange-400',
  red: 'bg-red-500/15 text-red-400',
  blue: 'bg-blue-500/15 text-blue-400',
};
const valueColor = {
  green: 'text-green-400',
  orange: 'text-orange-400',
  red: 'text-red-400',
  blue: 'text-blue-400',
};

const MetricCard = ({ glow, icon: Icon, label, value, subtitle, testId, pulse }) => (
  <div
    className={`relative bg-[var(--surface)] border ${glowClasses[glow]} rounded-[20px] p-5 group hover:-translate-y-1 transition-all duration-200 ${pulse ? 'pulse-hot' : ''}`}
    data-testid={testId}
  >
    <div className={`inline-flex p-2.5 rounded-[12px] ${iconBg[glow]} mb-4`}>
      <Icon size={20} weight="fill" />
    </div>
    <p className="text-[10px] uppercase tracking-[0.25em] font-black text-slate-500 mb-1">{label}</p>
    <p className={`font-heading text-3xl md:text-4xl font-black tracking-tighter ${valueColor[glow]}`}>{value}</p>
    {subtitle && <p className="text-[11px] text-slate-500 mt-1.5 font-medium">{subtitle}</p>}
  </div>
);

const SecondaryCard = ({ icon: Icon, title, value, testId }) => (
  <div className="bg-[var(--surface)] border border-[var(--border)] p-4 rounded-[16px] hover:border-[var(--border)] transition-all" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <div className="p-1.5 rounded-[10px] bg-white/5">
        <Icon size={16} weight="bold" className="text-slate-400" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">{title}</p>
    </div>
    <p className="font-heading text-2xl font-black tracking-tight text-white">{value}</p>
  </div>
);

const PerfCard = ({ icon: Icon, colorClass, title, value, note, testId }) => (
  <div className="bg-[var(--surface)] border border-[var(--border)] p-5 rounded-[16px]" data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2.5 rounded-[12px] ${iconBg[colorClass]}`}>
        <Icon size={20} weight="bold" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-500">{title}</p>
        <p className={`font-heading text-2xl font-black tracking-tight ${valueColor[colorClass]}`}>{value}</p>
      </div>
    </div>
    <p className="text-[11px] text-slate-500 font-medium">{note}</p>
  </div>
);

export default Dashboard;
